// Phase 16.10 Plan 01 — shared Fanbasis test-charge helper (D-07).
// Single source of truth for the edge-fn body shape.

import { supabase } from "@/integrations/supabase/client";

export interface ChargeableClient {
  id: string;
  company_name: string;
  package: "elite" | "standard" | null;
  fanbasis_customer_id: string;
  primary_payment_method: {
    id: string;
    fanbasis_payment_method_id: string;
    last4: string;
    brand: string | null;
  };
}

export type InvokeTestChargeResult =
  | { ok: true; chargeId: string }
  | { ok: false; error: string };

export async function invokeTestCharge({
  client,
  paymentMethod,
  amountCents,
}: {
  client: Pick<ChargeableClient, "id" | "company_name" | "fanbasis_customer_id">;
  paymentMethod: { fanbasis_payment_method_id: string };
  amountCents?: number;
}): Promise<InvokeTestChargeResult> {
  const amount = amountCents ?? 100;
  try {
    const { data, error } = await supabase.functions.invoke(
      "fanbasis-test-charge",
      {
        body: {
          customerId: client.fanbasis_customer_id,
          paymentMethodId: paymentMethod.fanbasis_payment_method_id,
          amount,
        },
      },
    );

    if (error) {
      let bodyError: string | null = null;
      try {
        const ctx = (error as { context?: Response }).context;
        if (ctx && typeof ctx.json === "function") {
          const body = (await ctx.json()) as {
            error?: string;
            fanbasis_code?: string;
            fanbasis_status?: number;
            message?: string;
          };
          // Map the stable consent_pending code to a friendly, actionable
          // message so admins stop retrying during the Fanbasis support window.
          if (body?.fanbasis_code === "consent_pending") {
            return {
              ok: false,
              error:
                "Fanbasis hasn't enabled direct charges for this account yet (support ticket pending)",
            };
          }
          bodyError =
            body?.error ??
            (body?.fanbasis_code
              ? `${body.fanbasis_code}${body.message ? `: ${body.message}` : ""}`
              : null) ??
            body?.message ??
            null;
        }
      } catch {
        /* body not JSON or already consumed — fall through to error.message */
      }
      return { ok: false, error: bodyError ?? error.message ?? "Charge failed" };
    }
    if (data?.success === true && data?.data?.id) {
      return { ok: true, chargeId: data.data.id };
    }
    return { ok: false, error: data?.error ?? "Charge failed" };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : "Charge failed";
    return { ok: false, error: message };
  }
}
